/*
 *  Copyright (C) GridGain Systems. All Rights Reserved.
 *  _________        _____ __________________        _____
 *  __  ____/___________(_)______  /__  ____/______ ____(_)_______
 *  _  / __  __  ___/__  / _  __  / _  / __  _  __ `/__  / __  __ \
 *  / /_/ /  _  /    _  /  / /_/ /  / /_/ /  / /_/ / _  /  _  / / /
 *  \____/   /_/     /_/   \_,__/   \____/   \__,_/  /_/   /_/ /_/
 */

import { ColumnApi } from '@ag-grid-community/core';
import { EventEmitter, TemplateRef } from '@angular/core';
import { DashboardMetadata, QueryWidgetSettings, QueryWidgetType } from '@app/core/types/index';
import { assertNever } from '@shared/domain/assert';
import { WidgetType } from '@shared/types/common';
import { MetricNameFragments } from '@shared/types/metrics';
import { NodeBulkSelector, NodeConsistentId } from '@shared/types/node';
import { isNil } from 'lodash-es';

export type Widget =
  | GridWidget
  | ChartWidget
  | HeatMapWidget
  | HistogramWidget
  | NodesWidget
  | RebalanceWidget
  | GaugeChartWidget
  | CachesWidget
  | RemoteReceiverConnectionsWidget
  | RemoteSendersWidget
  | SingleMetricValueWidget
  | SystemViewWidget
  | SqlQueryWidget;

export type WidgetMetadata<T extends WidgetType> = WidgetMetadataBasedOnEnum[T];

export enum WidgetParameterState {
  APPLIED,
  DISABLED,
  NOT_APPLIED,
}

export interface WidgetComponent<T extends WidgetType> {
  metadata: WidgetMetadata<T>;
  metadataChange: EventEmitter<WidgetMetadata<T>>;
  dashboardMetadataChange?: EventEmitter<Partial<DashboardMetadata>>;
  optionTemplateChange?: EventEmitter<TemplateRef<any>>;
  subtitleTemplateChange?: EventEmitter<TemplateRef<any>>;
  canRender: EventEmitter<boolean>;
  parametersStateChange: EventEmitter<WidgetParameterState>;
}

interface WidgetMetadataBasedOnEnum {
  [WidgetType.heatMap]: HeatMapWidgetMetadata;
  [WidgetType.gaugeChart]: GaugeWidgetMetadata;
  [WidgetType.metricsChart]: MetricChartWidgetMetadata;
  [WidgetType.metricsGrid]: MetricsGridWidgetMetadata;
  [WidgetType.metricsHistogram]: HistogramWidgetMetadata;
  [WidgetType.nodes]: NodesWidgetMetadata;
  [WidgetType.rebalance]: RebalanceWidgetMetadata;
  [WidgetType.caches]: CachesWidgetMetadata;
  [WidgetType.remoteReceiverConnections]: RemoteReceiverConnectionsWidgetMetadata;
  [WidgetType.remoteSenders]: RemoteSendersWidgetMetadata;
  [WidgetType.metricSingleValue]: SingleMetricValueWidgetMetadata;
  [WidgetType.systemView]: QueryWidgetMetadata;
  [WidgetType.sqlQuery]: QueryWidgetMetadata;
}

type GridWidget = WidgetBase<WidgetType.metricsGrid>;
type ChartWidget = WidgetBase<WidgetType.metricsChart>;
type HeatMapWidget = WidgetBase<WidgetType.heatMap>;
type HistogramWidget = WidgetBase<WidgetType.metricsHistogram>;
type NodesWidget = WidgetBase<WidgetType.nodes>;
type CachesWidget = WidgetBase<WidgetType.caches>;
type RebalanceWidget = WidgetBase<WidgetType.rebalance>;
type GaugeChartWidget = WidgetBase<WidgetType.gaugeChart>;
type RemoteReceiverConnectionsWidget = WidgetBase<WidgetType.remoteReceiverConnections>;
type RemoteSendersWidget = WidgetBase<WidgetType.remoteSenders>;
type SingleMetricValueWidget = WidgetBase<WidgetType.metricSingleValue>;
type SystemViewWidget = WidgetBase<WidgetType.systemView>;
type SqlQueryWidget = WidgetBase<WidgetType.sqlQuery>;

interface WidgetBase<T extends WidgetType> {
  id: string;
  metadata: WidgetMetadata<T>;
  component: T;
}

export type GridLayoutItem = {
  x: number;
  y: number;
  h: number;
  w: number;
  [propName: string]: any;
};

export type GridsterItem = {
  x: number;
  y: number;
  rows: number;
  cols: number;
  [propName: string]: any;
};

interface WidgetMetadataBase {
  title: string;
  gridster: GridsterItem;
}

export type GridWidgetMetadata = { agGrid?: AgGridMetadata };

interface AgGridMetadata {
  columnState?: ReturnType<ColumnApi['getColumnState']>;
}

export function isMultipleMetricsWidget(widget: Widget): widget is MultipleMetricsWidget {
  const { component } = widget;
  switch (component) {
    case WidgetType.metricsGrid:
    case WidgetType.metricsChart:
    case WidgetType.metricsHistogram:
      return true;
    case WidgetType.gaugeChart:
    case WidgetType.heatMap:
    case WidgetType.metricSingleValue:
    case WidgetType.nodes:
    case WidgetType.rebalance:
    case WidgetType.caches:
    case WidgetType.remoteReceiverConnections:
    case WidgetType.remoteSenders:
    case WidgetType.systemView:
    case WidgetType.sqlQuery:
      return false;
    default:
      return assertNever(component);
  }
}

export function isSingleMetricWidget(widget: Widget): widget is SingleMetricWidget {
  const { component } = widget;
  switch (component) {
    case WidgetType.gaugeChart:
    case WidgetType.heatMap:
    case WidgetType.metricSingleValue:
      return true;
    case WidgetType.metricsGrid:
    case WidgetType.metricsChart:
    case WidgetType.metricsHistogram:
    case WidgetType.nodes:
    case WidgetType.rebalance:
    case WidgetType.caches:
    case WidgetType.remoteReceiverConnections:
    case WidgetType.remoteSenders:
    case WidgetType.systemView:
    case WidgetType.sqlQuery:
      return false;
    default:
      return assertNever(component);
  }
}

export function isQueryWidget(widget: Widget): widget is QueryWidget {
  return 'queryWidgetType' in widget.metadata && !isNil(widget.metadata.queryWidgetType);
}

export type MultipleMetricsWidget = GridWidget | ChartWidget | HistogramWidget;

export type SingleMetricWidget = HeatMapWidget | GaugeChartWidget;

export type MetricWidget = MultipleMetricsWidget | SingleMetricWidget;

export type QueryWidget = SystemViewWidget | SqlQueryWidget;

export type MetricSelectSingleValue = {
  template: MetricNameFragments;
  nodeConsistentIds: (NodeBulkSelector | NodeConsistentId)[];
};

export interface MultipleMetricsMetadata {
  metrics: MetricSelectSingleValue[];
}

export interface SingleMetricMetadata {
  metric: MetricSelectSingleValue | null;
}

type MetricsGridWidgetMetadata = WidgetMetadataBase & GridWidgetMetadata & MultipleMetricsMetadata;

type NodesWidgetMetadata = WidgetMetadataBase &
  GridWidgetMetadata & { showOfflineNodes: boolean; showSearch?: boolean };

type CachesWidgetMetadata = WidgetMetadataBase & GridWidgetMetadata;

type RemoteReceiverConnectionsWidgetMetadata = WidgetMetadataBase;

type RemoteSendersWidgetMetadata = WidgetMetadataBase;

export type BreakpointMetricLimitMapKey = 'min' | 'max';

export type BreakpointMetadata = {
  breakpoints: [number, number];
  invertColorScale?: boolean;
  metricsMapping?: BreakpointMetricLimitMapKey[];
} & Partial<MultipleMetricsMetadata>;

type LegendMetadata = {
  hideLegend: boolean;
};

type HeatMapWidgetMetadata = WidgetMetadataBase & SingleMetricMetadata & BreakpointMetadata;

type GaugeWidgetMetadata = WidgetMetadataBase &
  SingleMetricMetadata &
  BreakpointMetadata & {
    showPercentage: boolean;
  };

type SingleMetricValueWidgetMetadata = WidgetMetadataBase & SingleMetricMetadata;

type HistogramWidgetMetadata = WidgetMetadataBase & MultipleMetricsMetadata & LegendMetadata;

type MetricChartWidgetMetadata = WidgetMetadataBase & MultipleMetricsMetadata & LegendMetadata;

type RebalanceWidgetMetadata = WidgetMetadataBase & { showProgress: boolean; showVersions: boolean };

export type QueryMetadata = {
  showInformation: boolean;
  showSearch: boolean;
  lastUpdate: null | number;
};

export type QueryWidgetMetadata = WidgetMetadataBase &
  GridWidgetMetadata &
  QueryWidgetSettings<QueryWidgetType> &
  QueryMetadata;
